import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { useOrgStore } from '../store/useOrgStore';
import { useAuditData, type AuditStepKey } from '../hooks/useAuditData';

const STEP_LABELS: Record<AuditStepKey, string> = {
  overview: 'Fetching org metadata',
  objects: 'Mapping objects & fields',
  flows: 'Auditing flows & automation',
  users: 'Analysing users',
  permissions: 'Checking permissions',
  techdebt: 'Detecting tech debt',
  foundations: 'Scanning Foundations AI features',
  email: 'Auditing email deliverability',
  datacloud: 'Computing Data Cloud readiness',
  activity: 'Analysing user activity & login patterns',
  licenses: 'Inventorying licences',
  apps: 'Mapping installed apps & integrations',
  storage: 'Profiling storage hotspots',
  datahygiene: 'Auditing data hygiene',
  healthscore: 'Calculating health score',
};

const STEP_ORDER: AuditStepKey[] = [
  'overview',
  'objects',
  'flows',
  'users',
  'permissions',
  'techdebt',
  'foundations',
  'email',
  'datacloud',
  'activity',
  'licenses',
  'apps',
  'storage',
  'datahygiene',
  'healthscore',
];

export function ConnectingPage() {
  const navigate = useNavigate();
  const { status, loading } = useAuthStatus();
  const setIdentity = useOrgStore((s) => s.setIdentity);
  const { run, state } = useAuditData();
  const startedRef = useRef(false);

  useEffect(() => {
    // Wait for the role. The audit must never start on a guess, so nothing
    // below runs while the status call is still in flight.
    if (loading) return;
    if (!status?.connected) {
      navigate('/connect', { replace: true });
      return;
    }
    // The live audit belongs to the client admin alone since TYR-216. A
    // consultant who types this URL gets the client scans instead of a
    // 15-endpoint scan of their own org; a session with no role signs in again.
    if (status.role !== 'client-admin') {
      navigate(status.role === 'consultant' ? '/internal/leads' : '/connect', { replace: true });
      return;
    }
    setIdentity({
      orgUrl: status.orgUrl,
      orgName: status.orgName,
      edition: status.orgEdition,
      username: status.username,
    });

    if (startedRef.current) return;
    startedRef.current = true;

    run().then((r) => {
      if (r.ok) {
        navigate('/hub', { replace: true });
      } else {
        toast.error(`Audit failed for: ${r.failed.join(', ')}`);
      }
    });
  }, [status, loading, navigate, setIdentity, run]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-xl w-full"
      >
        <div className="text-xs uppercase tracking-widest text-grow mb-3">Scanning org</div>
        <h1 className="text-3xl font-semibold text-crux-light">
          {status?.orgName ?? 'Connecting…'}
          {status?.orgType ? <span className="text-neutral-500 ml-2 text-base">({status.orgType})</span> : null}
        </h1>
        {status?.username ? (
          <p className="text-sm text-neutral-500 mt-1">Connected as: {status.username}</p>
        ) : null}

        <motion.ul
          className="mt-10 space-y-3 text-sm"
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.06, delayChildren: 0.15 } },
          }}
        >
          {STEP_ORDER.map((key) => {
            const status = state.steps[key];
            const error = state.errors[key];
            return (
              <motion.li
                key={key}
                className="flex items-center gap-3"
                variants={{
                  hidden: { opacity: 0, x: -8 },
                  visible: { opacity: 1, x: 0, transition: { duration: 0.25 } },
                }}
              >
                <span
                  className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                    status === 'done'
                      ? 'bg-grow/15 border-grow text-grow'
                      : status === 'active'
                        ? 'border-grow text-grow'
                        : status === 'error'
                          ? 'bg-sunset/15 border-sunset text-sunset'
                          : 'border-border text-neutral-600'
                  }`}
                >
                  {status === 'done' ? (
                    <Check size={12} />
                  ) : status === 'active' ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : status === 'error' ? (
                    <X size={12} />
                  ) : null}
                </span>
                <span
                  className={
                    status === 'done' || status === 'active'
                      ? 'text-crux-light'
                      : status === 'error'
                        ? 'text-sunset'
                        : 'text-neutral-600'
                  }
                >
                  {STEP_LABELS[key]}
                  {error ? <span className="ml-2 text-xs text-sunset">({error})</span> : null}
                </span>
              </motion.li>
            );
          })}
        </motion.ul>

        <p className="text-xs text-neutral-600 mt-10">
          This typically takes 20 to 45 seconds depending on org size.
        </p>
      </motion.div>
    </div>
  );
}
